import React, { useState, useEffect } from "react";
import { Col, Row, Button, Form, Alert } from "react-bootstrap";
import { rankAndCategorizeVoices } from "../utils/SpeechUtils";
import { isMacOSOrWindowsNonEdge } from "../utils/BrowserUtils";
import CustomSlider from "./CustomSlider";
import "./AudioTab.css";
import { useFeature } from "../contexts/FeatureFlagContext";

// "Play Sample" button text
const sampleText = "The quick brown fox jumps over the lazy dog.";

function AudioTab(props) {
  const [voiceGroups, setVoiceGroups] = useState({
    highQuality: [],
    standard: [],
    other: [],
  });

  useEffect(() => {
    const voiceGroupRankings = rankAndCategorizeVoices(
      props.voices,
      [window.navigator.language, props.language],
      "en",
    );
    setVoiceGroups(voiceGroupRankings);
  }, [props.voices, props.language]);

  const handlePlaySampleClicked = () => {
    props.onVoiceSampleRequested && props.onVoiceSampleRequested();

    // create a new utterance
    const sampleUtterance = new SpeechSynthesisUtterance(sampleText);

    // get the voice and rate settings
    sampleUtterance.voice = window.speechSynthesis
      .getVoices()
      .find((f) => f.voiceURI === props.voice);
    sampleUtterance.rate = props.audioSpeed;

    // voice the sample
    window.speechSynthesis.speak(sampleUtterance);
  };

  const { highQuality, standard, other } = voiceGroups;
  const allVoices = [...highQuality, ...standard, ...other];

  const renderOptions = (voices) =>
    voices?.map((f) => (
      <option value={f.voiceURI} key={f.voiceURI}>
        {f.name}
      </option>
    ));

  const useGroups =
    [highQuality, standard, other].filter((g) => g.length > 0).length >= 2;

  const selectedVoiceUri = (
    allVoices.find((f) => f.voiceURI === props.voice) || allVoices[0]
  )?.voiceURI;
  const voiceMenuLabel = "voiceMenuLabel";

  return (
    <>
      <Row>
        {isMacOSOrWindowsNonEdge() && (
          <Col xs={12}>
            <small>
              For the best voice quality, try opening Bookshare Reader in
              Microsoft Edge, which includes high-quality natural voices.
            </small>
          </Col>
        )}
      </Row>
      <Row className="voice-row">
        <Col xs={12} sm={3}>
          <div className="setting-header">
            <strong id={voiceMenuLabel}>Voice</strong>
          </div>
        </Col>
        <Col xs={12} sm={9} className="voice-dropdown-col">
          <div className="voice-select-wrapper">
            <select
              className="voice-dropdown"
              value={selectedVoiceUri}
              onChange={(evt) => props.onVoiceSelect(evt.target.value)}
              aria-labelledby={voiceMenuLabel}
            >
              {useGroups ? (
                <>
                  {highQuality.length > 0 && (
                    <optgroup role="group" label="High Quality">
                      {renderOptions(highQuality)}
                    </optgroup>
                  )}
                  {standard.length > 0 && (
                    <optgroup role="group" label="Standard">
                      {renderOptions(standard)}
                    </optgroup>
                  )}
                  {other.length > 0 && (
                    <optgroup role="group" label="Other">
                      {renderOptions(other)}
                    </optgroup>
                  )}
                </>
              ) : (
                renderOptions(allVoices)
              )}
            </select>
          </div>
        </Col>
      </Row>
      <br />
      <Row>
        {props.isGoogleVoice && (
          <Col xs={12}>
            <Alert variant="warning">
              Google voices sound more natural but do not support word-by-word
              highlighting. Sentence highlighting is still available.
            </Alert>
          </Col>
        )}
      </Row>
      <br />
      <Row>
        <Col>
          <CustomSlider
            name="audioSpeed"
            label="Audio Speed"
            value={props.audioSpeed}
            options={props.audioSpeedOptions}
            onChange={props.onAudioSpeedChange}
            incrementButtonLabel={"Increase Audio Speed"}
            decrementButtonLabel={"Decrease Audio Speed"}
            defaultButtonLabel={"Set to Normal Speed"}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={{ offset: 4 }} className="play-sample-button-col">
          <Button
            className="play-sample-button"
            onClick={handlePlaySampleClicked}
            aria-label="Play Voice Sample"
          >
            <Row>
              <Col>Play Sample</Col>
            </Row>
          </Button>
        </Col>
      </Row>
      <br />
      <Row>
        <Col xs={12} sm={3}>
          <div className="setting-header">
            <strong id="voicing-options">Voicing Options</strong>
          </div>
        </Col>
        <Col xs={12} sm={9} role="group" aria-labelledby="voicing-options">
          <Form.Check
            type="checkbox"
            checked={props.voicePageNumbers}
            label="Voice Page Numbers"
            onChange={(evt) =>
              props.onVoicePageNumberChange(evt.target.checked)
            }
            id="voice_page_numbers_checkbox"
          />
          <Form.Check
            type="checkbox"
            checked={props.voiceImageDescriptions}
            label="Voice Image Descriptions"
            onChange={(evt) =>
              props.onVoiceImageDescriptionChange(evt.target.checked)
            }
            id="voice_image_descriptions_checkbox"
          />
          {useFeature("annotations") && (
            <Form.Check
              type="checkbox"
              checked={props.readAnnotations}
              label="Voice Annotations"
              onChange={(evt) =>
                props.onReadAnnotationsChanged(evt.target.checked)
              }
              id="read_annotations_checkbox"
            />
          )}
        </Col>
      </Row>
      <br />
    </>
  );
}

export default AudioTab;
